import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import type { PermissionKey } from '@charisma/shared';

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
      },
      {
        path: 'employees',
        name: 'employees',
        component: () => import('@/views/employees/EmployeeListView.vue'),
        meta: { requiresAuth: true, permission: 'employee:read' },
      },
      {
        path: 'employees/new',
        name: 'employee-create',
        component: () => import('@/views/employees/EmployeeFormView.vue'),
        meta: { requiresAuth: true, permission: 'employee:write' },
      },
      {
        path: 'employees/:id/edit',
        name: 'employee-edit',
        component: () => import('@/views/employees/EmployeeFormView.vue'),
        meta: { requiresAuth: true, permission: 'employee:write' },
      },
      {
        path: 'users',
        name: 'users',
        component: () => import('@/views/users/UserListView.vue'),
        meta: { requiresAuth: true, permission: 'user:manage' },
      },
      {
        path: 'leave',
        name: 'leave',
        component: () => import('@/views/leave/LeaveView.vue'),
        meta: { requiresAuth: true, permission: 'leave:read' },
      },
      {
        path: 'contact',
        name: 'contact',
        component: () => import('@/views/contact/ContactListView.vue'),
        meta: { requiresAuth: true, permission: 'contact:read' },
      },
      {
        path: 'system-emails',
        name: 'system-emails',
        component: () => import('@/views/system-email/SystemEmailView.vue'),
        meta: { requiresAuth: true, permission: 'system-email:read' },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to) => {
  const auth = useAuthStore();
  if (to.meta.requiresAuth !== false && !auth.isAuthenticated) {
    return { name: 'login' };
  }
  if (to.name === 'login' && auth.isAuthenticated) {
    return { name: 'dashboard' };
  }
  const perm = to.meta.permission as string | undefined;
  if (perm && !auth.hasPermission(perm as PermissionKey)) {
    return { name: 'dashboard' };
  }
  return true;
});
